import { useRef, useState } from 'react';
import { Box, Button, HStack, Image, Text, VStack } from '@chakra-ui/react';
import { errorMessage } from './estimateApi';

const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

/**
 * The cover photo. Uploading replaces the current one; removing falls back
 * to the roof graphic in the company color. Shares `state` with
 * EstimatePhotos ({ photos, order, coverKey }), and the cover photo is never
 * shown there. Every change is saved immediately.
 */
const EstimateCoverPhoto = ({ api, state, onChange, isDisabled, toast }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const cover = state.photos.find(photo => photo.key === state.coverKey);

  const fail = (title, error) =>
    toast({
      title,
      description: errorMessage(error),
      status: 'error',
      duration: 5000,
      isClosable: true,
    });

  const upload = async e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/') || file.size > MAX_UPLOAD_BYTES) {
      toast({
        title: `"${file.name}" can't be used`,
        description: 'The cover photo must be an image under 20 MB.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setBusy(true);
    try {
      const form = new FormData();
      form.append('files', file);
      form.append('cover', 'true');
      form.append('username', api.username);
      onChange(await api.post('/api/pdf/photos', form));
    } catch (error) {
      fail('Upload failed', error);
    }
    setBusy(false);
  };

  const remove = async () => {
    setBusy(true);
    try {
      onChange(await api.post('/api/pdf/photos/remove', { key: cover.key }));
    } catch (error) {
      fail('Could not remove the cover photo', error);
    }
    setBusy(false);
  };

  return (
    <HStack align="center" spacing={4}>
      <Box
        w="120px"
        h="90px"
        flexShrink={0}
        borderWidth="1px"
        borderRadius="md"
        overflow="hidden"
        bg="gray.50"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {cover?.url ? (
          <Image src={cover.url} alt="" w="100%" h="100%" objectFit="cover" />
        ) : (
          <Text fontSize="xs" color="gray.400">
            No photo
          </Text>
        )}
      </Box>
      <VStack align="flex-start" spacing={2}>
        {!cover && (
          <Text fontSize="xs" color="gray.500">
            By default, a roof graphic in your company color is used.
          </Text>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={upload}
          style={{ display: 'none' }}
        />
        <HStack spacing={2}>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={() => inputRef.current.click()}
            isLoading={busy}
            isDisabled={isDisabled}
          >
            {cover ? 'Replace' : 'Add'}
          </Button>
          {cover && (
            <Button
              size="sm"
              variant="outline"
              onClick={remove}
              isDisabled={isDisabled || busy}
            >
              Remove
            </Button>
          )}
        </HStack>
      </VStack>
    </HStack>
  );
};

export default EstimateCoverPhoto;
